import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { SITE_LABEL } from "@/components/Layout";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Lock, Unlock, KeyRound, UserX, UserCheck, Plus, Mail, AlertCircle, MapPin, DollarSign, UtensilsCrossed, ShoppingCart, Receipt, ChefHat, CalendarClock } from "lucide-react";
import { Pagination, usePagination } from "@/components/Pagination";

const PLACEHOLDER_EMAIL_SUFFIX = "@xero-import.local";
const isPlaceholderEmail = (e) => typeof e === "string" && e.toLowerCase().endsWith(PLACEHOLDER_EMAIL_SUFFIX);

/** Pill colour for a user's role (SUPERADMIN/ADMIN/MANAGER/EMPLOYEE). */
function roleBadgeClass(role) {
  if (role === "SUPERADMIN") return "text-[10px] border-[#C9A84C] text-[#7a6420] bg-[#C9A84C]/10";
  if (role === "ADMIN") return "text-[10px] border-[#1A2744] text-[#1A2744] bg-[#1A2744]/5";
  if (role === "MANAGER") return "text-[10px] border-sky-400 text-sky-700 bg-sky-50";
  return "text-[10px]";
}

function CreateEmployeeDialog({ onCreated }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", employee_code: "", role: "EMPLOYEE",
    primary_site: "SITE_1", employment_type: "FULL_TIME",
    start_date: new Date().toISOString().slice(0, 10),
    hourly_rate: "", xero_employee_id: "", pin: "",
  });
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        hourly_rate: form.hourly_rate ? parseFloat(form.hourly_rate) : null,
        pin: form.pin || null,
      };
      const { data } = await api.post("/admin/users", payload);
      setCreated({ name: data.user.name, role: data.user.role, temp_password: data.temp_password, pin: data.pin });
      toast.success(`Employee created: ${data.user.name}`);
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setCreated(null); }}>
      <DialogTrigger asChild>
        <Button className="bg-[#1A2744] text-white hover:bg-[#263A63]" data-testid="create-employee-button">
          <Plus className="h-4 w-4 mr-1" /> Add employee
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>New employee</DialogTitle></DialogHeader>
        {created ? (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Share these credentials securely with <strong>{created.name}</strong>.
            </p>
            {created.role === "EMPLOYEE" && created.pin && (
              <div>
                <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold mb-1">Staff PIN (used with employee code)</div>
                <div className="p-3 bg-[#1A2744] text-[#C9A84C] rounded-lg font-mono text-3xl text-center tracking-[0.4em] font-bold" data-testid="created-pin-display">
                  {created.pin}
                </div>
              </div>
            )}
            <div>
              <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold mb-1">Temporary password (for email sign-in, managers only)</div>
              <div className="p-3 bg-[#C9A84C]/10 border border-[#C9A84C]/40 rounded-lg font-mono text-lg text-center" data-testid="temp-password-display">
                {created.temp_password}
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="emp-form-name" /></div>
              <div><Label>Employee code</Label><Input value={form.employee_code} onChange={(e) => setForm({ ...form, employee_code: e.target.value.toUpperCase() })} placeholder="e.g. CHCH005" data-testid="emp-form-code" /></div>
            </div>
            <div><Label>Email</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="emp-form-email" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="emp-form-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="EMPLOYEE">Employee</SelectItem>
                    <SelectItem value="MANAGER">Manager</SelectItem>
                    <SelectItem value="ADMIN">Admin</SelectItem>
                    {user?.role === "SUPERADMIN" && <SelectItem value="SUPERADMIN">Super Admin</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Primary site</Label>
                <Select value={form.primary_site} onValueChange={(v) => setForm({ ...form, primary_site: v })}>
                  <SelectTrigger data-testid="emp-form-site"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SITE_1">Christchurch</SelectItem>
                    <SelectItem value="SITE_2">Wellington</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Employment</Label>
                <Select value={form.employment_type} onValueChange={(v) => setForm({ ...form, employment_type: v })}>
                  <SelectTrigger data-testid="emp-form-employment"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="FULL_TIME">Full-time</SelectItem>
                    <SelectItem value="PART_TIME">Part-time</SelectItem>
                    <SelectItem value="CASUAL">Casual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Hourly rate (NZD)</Label><Input type="number" step="0.25" value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} data-testid="emp-form-rate" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Start date</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="emp-form-start" /></div>
              <div><Label>Xero employee ID</Label><Input value={form.xero_employee_id} onChange={(e) => setForm({ ...form, xero_employee_id: e.target.value })} data-testid="emp-form-xero" /></div>
            </div>
            {form.role === "EMPLOYEE" && (
              <div>
                <Label>Staff PIN (4 digits — leave blank to auto-generate)</Label>
                <Input
                  inputMode="numeric"
                  maxLength={4}
                  placeholder="auto"
                  value={form.pin}
                  onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, "").slice(0, 4) })}
                  data-testid="emp-form-pin"
                />
              </div>
            )}
            <DialogFooter>
              <Button type="submit" disabled={busy} className="bg-[#1A2744] text-white" data-testid="emp-form-submit">
                {busy ? "Creating…" : "Create employee"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function EditEmailButton({ userRow, onDone }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(userRow.email || "");
  const [busy, setBusy] = useState(false);
  const placeholder = isPlaceholderEmail(userRow.email);

  const submit = async (e) => {
    e.preventDefault();
    const value = email.trim().toLowerCase();
    if (!value || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      toast.error("Enter a valid email address");
      return;
    }
    if (value === (userRow.email || "").toLowerCase()) {
      toast.info("Email unchanged");
      setOpen(false);
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/admin/users/${userRow.id}`, { email: value });
      toast.success("Email updated");
      setOpen(false);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update email");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (v) setEmail(userRow.email || ""); }}>
      <DialogTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          className={placeholder ? "border-amber-300 text-amber-700 hover:bg-amber-50" : ""}
          data-testid={`edit-email-${userRow.id}`}
        >
          <Mail className="h-3.5 w-3.5 mr-1" /> {placeholder ? "Set email" : "Edit email"}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{placeholder ? "Set a real email address" : "Update email address"}</DialogTitle>
        </DialogHeader>
        {placeholder && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-xs">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              This employee was imported from Xero with a placeholder address
              (<span className="font-mono">{userRow.email}</span>). Replace it with
              their real email so they can receive password resets and manager
              email sign-in (admins/super-admins only).
            </span>
          </div>
        )}
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label htmlFor={`email-${userRow.id}`}>Email</Label>
            <Input
              id={`email-${userRow.id}`}
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="firstname.lastname@coffeelovers.nz"
              className="mt-1"
              data-testid={`edit-email-input-${userRow.id}`}
            />
            <p className="text-[11px] text-gray-500 mt-1">
              Must be a real, public email address — <span className="font-mono">@xero-import.local</span> placeholders are rejected.
            </p>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-[#1A2744] text-white" data-testid={`edit-email-save-${userRow.id}`}>
              {busy ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditSiteButton({ userRow, onDone }) {
  const [open, setOpen] = useState(false);
  const [site, setSite] = useState(userRow.primary_site || "SITE_1");
  // Allow-list of additional sites the employee can clock in/out at.
  // Primary site is implicit — they always retain access there. We store
  // the EXTRA sites in `sites[]` separately so the UI is clear about
  // permanent (primary) vs temporary (additional) access.
  const [extraSites, setExtraSites] = useState(
    (userRow.sites || []).filter((s) => s !== userRow.primary_site),
  );
  const [busy, setBusy] = useState(false);
  const sites = Object.keys(SITE_LABEL);
  const isEmployee = userRow.role === "EMPLOYEE";

  const toggleExtra = (code) => {
    setExtraSites((prev) =>
      prev.includes(code) ? prev.filter((s) => s !== code) : [...prev, code],
    );
  };

  const submit = async () => {
    const primaryChanged = site !== userRow.primary_site;
    const sortedNew = [...new Set([site, ...extraSites])].sort();
    const sortedOld = [...new Set([userRow.primary_site, ...(userRow.sites || [])])].sort();
    const accessChanged = JSON.stringify(sortedNew) !== JSON.stringify(sortedOld);
    if (!primaryChanged && !accessChanged) {
      toast.info("Nothing changed");
      setOpen(false);
      return;
    }
    setBusy(true);
    try {
      const payload = { primary_site: site, sites: sortedNew };
      await api.patch(`/admin/users/${userRow.id}`, payload);
      const labels = sortedNew.map((s) => SITE_LABEL[s] || s);
      toast.success(`Saved · primary ${SITE_LABEL[site] || site} · access [${labels.join(", ")}]`);
      setOpen(false);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update site access");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => {
      setOpen(v);
      if (v) {
        setSite(userRow.primary_site || "SITE_1");
        setExtraSites((userRow.sites || []).filter((s) => s !== userRow.primary_site));
      }
    }}>
      <DialogTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          data-testid={`edit-site-${userRow.id}`}
        >
          <MapPin className="h-3.5 w-3.5 mr-1" /> Site
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Site access for {userRow.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="text-xs text-gray-600">
            <div>
              Primary site: <span className="font-semibold text-[#1A2744]">{SITE_LABEL[userRow.primary_site] || userRow.primary_site}</span>
              {(userRow.sites || []).length > 0 && (
                <div className="text-[11px] mt-0.5">
                  Currently approved sites:{" "}
                  <span className="font-mono">{(userRow.sites || []).map((s) => SITE_LABEL[s] || s).join(", ")}</span>
                </div>
              )}
            </div>
            {userRow.imported_from_xero && (
              <p className="text-[11px] text-gray-500 mt-2">
                Xero-imported. The next sync may re-route the <em>primary</em> site if a Holiday-Group mapping
                resolves elsewhere; additional access stays as set here.
              </p>
            )}
          </div>

          <div>
            <Label htmlFor={`site-${userRow.id}`}>Primary site</Label>
            <Select value={site} onValueChange={(v) => {
              setSite(v);
              // Moving primary to a site that was in extras? Drop it from extras.
              setExtraSites((prev) => prev.filter((s) => s !== v));
            }}>
              <SelectTrigger className="mt-1" data-testid={`edit-site-select-${userRow.id}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {sites.map((code) => (
                  <SelectItem key={code} value={code}>
                    {SITE_LABEL[code]} <span className="text-gray-400 ml-1 font-mono">({code})</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-gray-500 mt-1">
              The site where this employee is normally rostered. Always has clock-in access.
            </p>
          </div>

          {isEmployee && (
            <div>
              <Label className="block mb-1">Additional clock-in access</Label>
              <div className="rounded-lg border border-gray-200 p-2 space-y-1.5">
                {sites.filter((c) => c !== site).map((code) => (
                  <label
                    key={code}
                    className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer"
                    data-testid={`extra-site-${code}-${userRow.id}`}
                  >
                    <input
                      type="checkbox"
                      checked={extraSites.includes(code)}
                      onChange={() => toggleExtra(code)}
                      className="accent-[#1A2744]"
                    />
                    <span className="text-sm">{SITE_LABEL[code]}</span>
                    <span className="text-[11px] text-gray-400 font-mono ml-auto">{code}</span>
                  </label>
                ))}
                {sites.length <= 1 && (
                  <div className="text-[11px] text-gray-500 italic px-2">
                    Only one site configured — nothing to grant.
                  </div>
                )}
              </div>
              <p className="text-[11px] text-gray-500 mt-1">
                Tick any cafe site this employee can also clock in/out at — e.g. for cross-site cover.
                Without a tick they&apos;ll receive HTTP 403 with a clear message when they try to clock at that site.
              </p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-[#1A2744] text-white" data-testid={`edit-site-save-${userRow.id}`}>
            {busy ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function RegenerateCodeButton({ userRow, onDone }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // {old_code, new_code, site}

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${userRow.id}/regenerate-code`);
      setResult(data);
      toast.success(`Code regenerated: ${data.old_code} → ${data.new_code}`);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Regenerate failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setResult(null); }}>
      <DialogTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          data-testid={`regen-code-${userRow.id}`}
          title={`Reissue code on ${SITE_LABEL[userRow.primary_site] || userRow.primary_site} prefix`}
        >
          <KeyRound className="h-3.5 w-3.5 mr-1" /> Code
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {result ? "Code regenerated" : `Regenerate code for ${userRow.name}?`}
          </DialogTitle>
        </DialogHeader>
        {!result && (
          <>
            <div className="text-sm text-gray-700 space-y-2">
              <p>
                Issues a fresh employee code on this employee&apos;s current site
                (<span className="font-semibold">{SITE_LABEL[userRow.primary_site] || userRow.primary_site}</span>).
              </p>
              <div className="bg-gray-50 border border-gray-200 rounded p-2 text-xs font-mono">
                Current: {userRow.employee_code || "—"}
              </div>
              <div className="text-[11px] text-gray-500">
                Use this if the existing code doesn&apos;t match the site
                (e.g. <code>CHCH014</code> on a Wellington staff member). Push to integrated POS
                fires automatically with the new code + the previous one set on
                <code> previous_employee_code</code>.
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={submit} disabled={busy} className="bg-[#1A2744] text-white" data-testid={`regen-code-confirm-${userRow.id}`}>
                {busy ? "Regenerating…" : "Regenerate"}
              </Button>
            </DialogFooter>
          </>
        )}
        {result && (
          <>
            <div className="space-y-2 text-sm">
              <div className="bg-emerald-50 border border-emerald-200 rounded p-3 font-mono text-xs">
                <div><span className="text-gray-500">Old:</span> {result.old_code || "—"}</div>
                <div><span className="text-gray-500">New:</span> <span className="text-emerald-700 font-bold">{result.new_code}</span></div>
                <div><span className="text-gray-500">Site:</span> {SITE_LABEL[result.site] || result.site}</div>
              </div>
              <p className="text-[11px] text-gray-500">
                The integrated POS (if any) has been notified with the new code on this employee.
              </p>
            </div>
            <DialogFooter>
              <Button onClick={() => setOpen(false)} className="bg-[#1A2744] text-white">Done</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}


function ResetPasswordButton({ id, onDone }) {
  const [pwd, setPwd] = useState(null);
  const [open, setOpen] = useState(false);
  const trigger = async () => {
    try {
      const { data } = await api.post(`/admin/users/${id}/reset-password`);
      setPwd(data.temp_password);
      setOpen(true);
      onDone();
    } catch (e) { toast.error("Failed"); }
  };
  return (
    <>
      <Button size="sm" variant="outline" onClick={trigger} data-testid={`reset-${id}`}>
        <KeyRound className="h-3.5 w-3.5 mr-1" /> Reset pwd
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Temporary password</DialogTitle></DialogHeader>
          <div className="p-3 bg-[#C9A84C]/10 border border-[#C9A84C]/40 rounded-lg font-mono text-lg text-center">{pwd}</div>
          <p className="text-xs text-gray-500">Share this securely. Employee must change on next login.</p>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ResetPinButton({ id, onDone }) {
  const [pin, setPin] = useState(null);
  const [open, setOpen] = useState(false);
  const trigger = async () => {
    try {
      const { data } = await api.post(`/admin/users/${id}/reset-pin`);
      setPin(data.pin);
      setOpen(true);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <>
      <Button size="sm" variant="outline" onClick={trigger} data-testid={`reset-pin-${id}`}>
        <KeyRound className="h-3.5 w-3.5 mr-1" /> Reset PIN
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New PIN</DialogTitle></DialogHeader>
          <div className="p-4 bg-[#1A2744] text-[#C9A84C] rounded-lg font-mono text-3xl text-center tracking-[0.4em] font-bold">{pin}</div>
          <p className="text-xs text-gray-500">Share this PIN with the employee. It replaces their previous PIN immediately.</p>
        </DialogContent>
      </Dialog>
    </>
  );
}

function SalesAccessToggle({ userRow, onDone }) {
  const [busy, setBusy] = useState(false);
  const on = !!userRow.can_view_sales || ["ADMIN", "SUPERADMIN"].includes(userRow.role);
  const alwaysOn = ["ADMIN", "SUPERADMIN"].includes(userRow.role);
  const toggle = async () => {
    if (alwaysOn) { toast.info("Admins always have Sales access."); return; }
    setBusy(true);
    try {
      await api.patch(`/admin/users/${userRow.id}/sales-access`, { can_view_sales: !on });
      toast.success(!on ? "Sales access granted" : "Sales access revoked");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Button
      size="sm"
      variant={on ? "default" : "outline"}
      onClick={toggle}
      disabled={busy || alwaysOn}
      className={on ? "bg-[#C9A84C] text-white hover:bg-[#C9A84C]/90" : ""}
      title={alwaysOn ? "Admins always see Sales" : on ? "Revoke Sales dashboard access" : "Grant Sales dashboard access"}
      data-testid={`sales-access-${userRow.id}`}
    >
      <DollarSign className="h-3.5 w-3.5 mr-1" /> Sales
    </Button>
  );
}

function TabAccessToggle({ userRow, flag, label, icon: Icon, onDone, alwaysRoles = ["ADMIN", "SUPERADMIN", "MANAGER"] }) {
  const [busy, setBusy] = useState(false);
  const alwaysOn = alwaysRoles.includes(userRow.role);
  const on = !!userRow[flag] || alwaysOn;
  const toggle = async () => {
    if (alwaysOn) { toast.info(`Admins and managers always have ${label} access.`); return; }
    setBusy(true);
    try {
      await api.patch(`/admin/users/${userRow.id}/tab-access`, { [flag]: !on });
      toast.success(!on ? `${label} access granted` : `${label} access revoked`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Button
      size="sm"
      variant={on ? "default" : "outline"}
      onClick={toggle}
      disabled={busy || alwaysOn}
      className={on ? "bg-[#C9A84C] text-white hover:bg-[#C9A84C]/90" : ""}
      title={alwaysOn ? `${label} tab always visible for this role` : on ? `Revoke ${label} tab access` : `Grant ${label} tab access`}
      data-testid={`${flag}-toggle-${userRow.id}`}
    >
      <Icon className="h-3.5 w-3.5 mr-1" /> {label}
    </Button>
  );
}

const WORK_DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function WorkPatternButton({ userRow, onDone }) {
  const [open, setOpen] = useState(false);
  const [days, setDays] = useState([]);
  const [hours, setHours] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setDays(userRow.work_days || [0, 1, 2, 3, 4]);
    setHours(userRow.contracted_weekly_hours != null ? String(userRow.contracted_weekly_hours) : "");
  }, [open, userRow.work_days, userRow.contracted_weekly_hours]);

  const toggleDay = (d) =>
    setDays((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d].sort((a, b) => a - b)));

  const save = async () => {
    setBusy(true);
    try {
      const payload = { work_days: days };
      if (hours !== "" && Number(hours) !== userRow.contracted_weekly_hours) {
        payload.contracted_weekly_hours = Number(hours);
      }
      await api.patch(`/admin/users/${userRow.id}`, payload);
      toast.success("Work pattern saved");
      setOpen(false);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const perDay = days.length && Number(hours) > 0 ? (Number(hours) / days.length).toFixed(2) : null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`work-pattern-${userRow.id}`}>
          <CalendarClock className="h-3.5 w-3.5 mr-1" /> Hours
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Contracted hours — {userRow.name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Contracted weekly hours</Label>
            <Input
              type="number"
              min="0"
              step="0.5"
              value={hours}
              placeholder="Synced from Xero"
              onChange={(e) => setHours(e.target.value)}
              data-testid={`work-pattern-hours-${userRow.id}`}
            />
            <p className="mt-1 text-[11px] text-gray-500">
              {userRow.contracted_hours_source === "XERO"
                ? "Currently synced from Xero — editing here overrides it until the next sync."
                : userRow.contracted_hours_source === "MANUAL"
                  ? "Set manually by an admin."
                  : "Not synced yet. Run “Sync contracted hours” on the Xero page, or set it here."}
            </p>
          </div>
          <div>
            <Label>Work days</Label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {WORK_DAY_NAMES.map((name, d) => (
                <Button
                  key={name}
                  type="button"
                  size="sm"
                  variant={days.includes(d) ? "default" : "outline"}
                  className={days.includes(d) ? "bg-[#1A2744] text-white" : ""}
                  onClick={() => toggleDay(d)}
                  data-testid={`work-day-${d}-${userRow.id}`}
                >
                  {name}
                </Button>
              ))}
            </div>
            <p className="mt-1 text-[11px] text-gray-500">
              Leave requests auto-fill hours on these days only.
              {perDay ? ` That's ${perDay}h per work day.` : ""}
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={busy} className="bg-[#1A2744] text-white" data-testid={`work-pattern-save-${userRow.id}`}>
            {busy ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EmployeeActions({ u, load, call }) {
  return (
    <>
      <EditEmailButton userRow={u} onDone={load} />
      <EditSiteButton userRow={u} onDone={load} />
      <SalesAccessToggle userRow={u} onDone={load} />
      <TabAccessToggle userRow={u} flag="can_food_wastage" label="Wastage" icon={UtensilsCrossed} onDone={load} />
      <TabAccessToggle userRow={u} flag="can_ordering" label="Ordering" icon={ShoppingCart} onDone={load} />
      <TabAccessToggle userRow={u} flag="can_expenses" label="Expenses" icon={Receipt} onDone={load} />
      <TabAccessToggle
        userRow={u}
        flag="can_edit_recipes"
        label="Recipes"
        icon={ChefHat}
        onDone={load}
        alwaysRoles={["ADMIN", "SUPERADMIN"]}
      />
      <WorkPatternButton userRow={u} onDone={load} />
      {u.role === "EMPLOYEE" && <RegenerateCodeButton userRow={u} onDone={load} />}
      <ResetPasswordButton id={u.id} onDone={load} />
      {u.role === "EMPLOYEE" && <ResetPinButton id={u.id} onDone={load} />}
      {u.locked_at && <Button size="sm" variant="outline" onClick={() => call(`/admin/users/${u.id}/unlock`)} data-testid={`unlock-${u.id}`}><Unlock className="h-3.5 w-3.5 mr-1" /> Unlock</Button>}
      {u.is_active
        ? <Button size="sm" variant="outline" className="text-red-600" onClick={() => call(`/admin/users/${u.id}/deactivate`)} data-testid={`deactivate-${u.id}`}><UserX className="h-3.5 w-3.5 mr-1" /> Deactivate</Button>
        : <Button size="sm" variant="outline" onClick={() => call(`/admin/users/${u.id}/reactivate`)} data-testid={`reactivate-${u.id}`}><UserCheck className="h-3.5 w-3.5 mr-1" /> Reactivate</Button>
      }
    </>
  );
}

export default function AdminEmployees() {
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState("ALL");
  const [siteFilter, setSiteFilter] = useState("ALL");
  const [roleFilter, setRoleFilter] = useState("ALL");

  const load = useCallback(async () => {
    const params = {};
    if (siteFilter !== "ALL") params.site = siteFilter;
    if (roleFilter !== "ALL") params.role = roleFilter;
    const { data } = await api.get("/admin/users", { params });
    setUsers(data.users);
  }, [siteFilter, roleFilter]);

  useEffect(() => { load().catch(() => {}); }, [load]);

  const call = async (path) => {
    try { await api.post(path); toast.success("Done"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const matchesFilter = (u) => {
    if (filter === "ACTIVE") return u.is_active;
    if (filter === "LOCKED") return Boolean(u.locked_at);
    if (filter === "INACTIVE") return !u.is_active;
    return true; // "ALL"
  };
  const filtered = users.filter(matchesFilter);
  const pg = usePagination(filtered, 10);

  const roleCounts = users.reduce((acc, u) => {
    acc[u.role] = (acc[u.role] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="admin-employees-page">
      <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#1A2744]">Employees</h1>
          <p className="text-gray-500 mt-1">Add team members, reset passwords, unlock accounts.</p>
        </div>
        <CreateEmployeeDialog onCreated={load} />
      </header>

      <div className="flex flex-col md:flex-row md:items-end gap-3 md:gap-4 flex-wrap">
        <div className="flex flex-col gap-1">
          <Label className="text-[11px] uppercase tracking-widest text-gray-500">Site</Label>
          <Select value={siteFilter} onValueChange={setSiteFilter}>
            <SelectTrigger className="w-[180px]" data-testid="site-filter-trigger">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL" data-testid="site-filter-all">All sites</SelectItem>
              <SelectItem value="SITE_1" data-testid="site-filter-chch">Christchurch</SelectItem>
              <SelectItem value="SITE_2" data-testid="site-filter-wlg">Wellington</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-[11px] uppercase tracking-widest text-gray-500">Role</Label>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-[200px]" data-testid="role-filter-trigger">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL" data-testid="role-filter-all">All roles</SelectItem>
              <SelectItem value="EMPLOYEE" data-testid="role-filter-employee">Employees</SelectItem>
              <SelectItem value="MANAGER" data-testid="role-filter-manager">Managers</SelectItem>
              <SelectItem value="ADMIN" data-testid="role-filter-admin">Admins</SelectItem>
              <SelectItem value="SUPERADMIN" data-testid="role-filter-superadmin">Super admins</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1" />
        <div className="flex gap-2 flex-wrap">
          {["ALL", "ACTIVE", "LOCKED", "INACTIVE"].map((f) => (
            <Button
              key={f}
              size="sm"
              variant={filter === f ? "default" : "outline"}
              className={filter === f ? "bg-[#1A2744] text-white" : ""}
              onClick={() => setFilter(f)}
              data-testid={`filter-${f.toLowerCase()}`}
            >
              {f}
            </Button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 text-xs" data-testid="role-counts">
        <Badge variant="outline" className="border-[#1A2744]/30">
          {users.length} total
        </Badge>
        {["SUPERADMIN", "ADMIN", "MANAGER", "EMPLOYEE"].map((r) =>
          roleCounts[r] ? (
            <Badge key={r} variant="outline" className="border-gray-300" data-testid={`role-count-${r.toLowerCase()}`}>
              {roleCounts[r]} {r.toLowerCase()}{roleCounts[r] === 1 ? "" : "s"}
            </Badge>
          ) : null
        )}
      </div>

      <Card>
        <CardContent className="p-0">
          {/* Phones: a card per employee with the actions wrapped underneath,
              instead of an 800px-wide table you have to swipe through. */}
          <div className="md:hidden divide-y divide-gray-100 px-4" data-testid="admin-employees-cards">
            {pg.pageItems.length === 0 && <p className="py-8 text-center text-gray-500">No employees match.</p>}
            {pg.pageItems.map((u) => (
              <div key={u.id} className="py-3 space-y-2" data-testid={`employee-card-${u.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <Link
                      to={`/admin/employees/${u.id}`}
                      className="font-semibold text-[#1A2744] hover:text-[#C9A84C] underline-offset-4 hover:underline"
                    >
                      {u.name}
                    </Link>
                    <p className="text-xs text-gray-500 truncate">
                      {u.employee_code ? `${u.employee_code} · ` : ""}{SITE_LABEL[u.primary_site]}
                    </p>
                    <p className={`text-xs truncate ${isPlaceholderEmail(u.email) ? "text-amber-700" : "text-gray-500"}`}>
                      {u.email}
                    </p>
                  </div>
                  <div className="shrink-0 text-right space-y-1">
                    <Badge variant="outline" className={roleBadgeClass(u.role)}>{u.role}</Badge>
                    <div>
                      {!u.is_active ? <Badge className="bg-gray-200 text-gray-700">Inactive</Badge>
                        : u.locked_at ? <Badge className="bg-red-100 text-red-700">Locked</Badge>
                          : <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>}
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <EmployeeActions u={u} load={load} call={call} />
                </div>
              </div>
            ))}
          </div>
          <div className="hidden md:block overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Code</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Site</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pg.pageItems.map((u) => (
                <TableRow key={u.id}>
                  <TableCell className="font-medium">
                    <Link
                      to={`/admin/employees/${u.id}`}
                      className="text-[#1A2744] hover:text-[#C9A84C] hover:underline underline-offset-4"
                      data-testid={`emp-detail-link-${u.id}`}
                    >
                      {u.name}
                    </Link>
                  </TableCell>
                  <TableCell className="text-xs">
                    {isPlaceholderEmail(u.email) ? (
                      <div className="flex items-center gap-1.5 text-amber-700" data-testid={`email-placeholder-${u.id}`}>
                        <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                        <span className="font-mono truncate max-w-[200px]" title={u.email}>{u.email}</span>
                      </div>
                    ) : (
                      <span className="text-gray-500">{u.email}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs">{u.employee_code || "—"}</TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={roleBadgeClass(u.role)}
                      data-testid={`role-badge-${u.id}`}
                    >
                      {u.role}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-gray-500">{SITE_LABEL[u.primary_site]}</TableCell>
                  <TableCell>
                    {!u.is_active ? (
                      <div className="flex flex-col gap-0.5">
                        <Badge className="bg-gray-200 text-gray-700 w-fit">Inactive</Badge>
                        {u.deactivation_reason === "xero_terminated" && (
                          <span className="text-[10px] text-amber-700" data-testid={`xero-terminated-${u.id}`}>
                            Xero terminated{u.termination_date ? ` · ${u.termination_date}` : ""}
                          </span>
                        )}
                      </div>
                    ) : u.locked_at ? (
                      <Badge className="bg-red-100 text-red-700">Locked</Badge>
                    ) : (
                      <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>
                    )}
                  </TableCell>
                  <TableCell className="flex flex-wrap gap-1">
                    <EmployeeActions u={u} load={load} call={call} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
        </CardContent>
      </Card>
      <div className="px-1">
        <Pagination
          page={pg.page}
          pageCount={pg.pageCount}
          total={pg.total}
          pageSize={pg.pageSize}
          onPageChange={pg.setPage}
          idPrefix="employees"
        />
      </div>
    </div>
  );
}
